import { ReactElement, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph, Tag, ValidationMessage } from '@digdir/designsystemet-react';
import { ChevronDownIcon } from '@navikt/aksel-icons';
import type { TemplateFactor, TemplateFactorInput, TemplateLanguage } from '../../../../api/hms';
import { consequenceLabel } from '../../hmsFormat';
import { languageNameInText } from '../../hmsLanguages';
import { questionItemKey } from '../languagesView';
import {
  type NewQuestion,
  questionState,
  RECOMMENDED_QUESTIONS,
  toInput,
  withoutFactor,
  withoutQuestion,
  withQuestion,
} from '../riskFactorsView';
import { NewFactorForm } from './NewFactorForm';
import { QuestionForm } from './QuestionForm';
import layout from '../../hmsPage.module.css';
import classes from '../HRRiskFactors.module.css';

type Props = {
  factors: TemplateFactor[];
  current: TemplateFactor[];
  /** The languages each question is not translated to */
  missingLanguages: Map<string, TemplateLanguage[]>;
  title: string;
  locked: boolean;
  saving: boolean;
  onSave: (factors: TemplateFactorInput[], onSaved?: () => void) => void;
};

/** The question being added (editIndex null) or edited */
type QuestionTarget = { factorIndex: number; editIndex: number | null };

export const FactorList = ({ factors, current, missingLanguages, title, locked, saving, onSave }: Props): ReactElement => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [addingFactor, setAddingFactor] = useState(false);
  const [target, setTarget] = useState<QuestionTarget | null>(null);

  // Nothing can be edited while the draft waits for approval
  useEffect(() => {
    if (locked) {
      setAddingFactor(false);
      setTarget(null);
    }
  }, [locked]);

  const questionCount = factors.flatMap((factor) => factor.questions).length;
  const customCount = factors.filter((factor) => factor.source === 'CUSTOM').length;
  const isTarget = (factorIndex: number, editIndex: number | null) =>
    target?.factorIndex === factorIndex && target.editIndex === editIndex;

  const addFactor = (factor: TemplateFactorInput) => {
    const index = factors.length;
    onSave([...toInput(factors), factor], () => {
      setAddingFactor(false);
      setOpenIndex(index);
      setTarget({ factorIndex: index, editIndex: null });
    });
  };

  const saveQuestion = (question: NewQuestion) => {
    if (!target) return;
    onSave(withQuestion(factors, target.factorIndex, question, target.editIndex), () => setTarget(null));
  };

  return (
    <section aria-labelledby="hr-rf-list" className={classes.listCard}>
      <div className={classes.listHeader}>
        <div className={layout.cardHeading}>
          <Heading level={2} data-size="sm" id="hr-rf-list">
            {title}
          </Heading>
          <Paragraph data-size="sm" className={layout.cardIntro}>
            {t('hrRiskFactors.list.summary', { factors: factors.length, questions: questionCount, custom: customCount })}
          </Paragraph>
        </div>
        {!locked && (
          <Button variant="secondary" data-size="sm" onClick={() => setAddingFactor(true)}>
            {t('hrRiskFactors.list.addFactor')}
          </Button>
        )}
      </div>

      {addingFactor && <NewFactorForm saving={saving} onSubmit={addFactor} onCancel={() => setAddingFactor(false)} />}

      <ul className={classes.factors}>
        {factors.map((factor, index) => {
          const open = openIndex === index;
          const panelId = `hr-rf-factor-${index}`;
          const count = factor.questions.length;
          const few = count < RECOMMENDED_QUESTIONS;
          const isNew = !current.some((candidate) => candidate.key === factor.key);
          const adding = isTarget(index, null);
          return (
            <li key={factor.key} className={classes.factor}>
              <button
                type="button"
                className={classes.factorToggle}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => {
                  setOpenIndex(open ? null : index);
                  setTarget(null);
                }}
              >
                <span className={classes.factorName}>
                  <span className="ds-label">{factor.name}</span>
                  <Tag data-size="sm" data-color={factor.source === 'CUSTOM' ? 'accent' : 'neutral'}>
                    {t(factor.source === 'CUSTOM' ? 'hrRiskFactors.list.custom' : 'hrRiskFactors.list.standard')}
                  </Tag>
                  {isNew && (
                    <Tag data-size="sm" data-color="warning">
                      {t('hrRiskFactors.list.newInDraft')}
                    </Tag>
                  )}
                </span>
                <span className={classes.factorMeta}>
                  <span className="ds-paragraph" data-size="sm" data-few={few}>
                    {t('hrRiskFactors.list.questions', { count })}
                  </span>
                  <ChevronDownIcon aria-hidden className={classes.chevron} data-open={open} />
                </span>
              </button>
              {open && (
                <div id={panelId} className={classes.factorPanel}>
                  {factor.description && (
                    <Paragraph data-size="sm" className={layout.cardIntro}>
                      {factor.description}
                    </Paragraph>
                  )}
                  {factor.relevanceReason && (
                    <Paragraph data-size="sm" className={classes.relevance}>
                      <Trans
                        i18nKey="hrRiskFactors.list.relevance"
                        values={{ reason: factor.relevanceReason }}
                        components={{ strong: <strong /> }}
                      />
                    </Paragraph>
                  )}
                  <Paragraph data-size="xs" className={layout.subtle}>
                    {t('hrRiskFactors.list.dimensionAndConsequence', {
                      dimension: t(`hms.dimensions.${factor.dimension}`),
                      consequence: consequenceLabel(factor.consequence, t),
                    })}
                  </Paragraph>
                  {few && (
                    <ValidationMessage data-color="warning">{t('hrRiskFactors.list.fewQuestions')}</ValidationMessage>
                  )}
                  <ol className={classes.questions}>
                    {factor.questions.map((question, questionIndex) => {
                      const state = questionState(question, factor.key, current);
                      const missing = missingLanguages.get(questionItemKey(factor.key, question.key)) ?? [];
                      const editing = isTarget(index, questionIndex);
                      return (
                        <li key={question.key} className={classes.question}>
                          <span className={classes.questionBody}>
                            <span className="ds-paragraph" data-size="sm" data-weight="medium">
                              {question.text}
                            </span>
                            <span className={classes.questionTags}>
                              <Tag data-size="sm" data-color={question.highIsGood ? 'neutral' : 'warning'}>
                                {t(question.highIsGood ? 'hrRiskFactors.list.highIsGood' : 'hrRiskFactors.list.highIsProblem')}
                              </Tag>
                              {question.isIncident && (
                                <Tag data-size="sm" data-color="info">
                                  {t('hrRiskFactors.list.incident')}
                                </Tag>
                              )}
                              {state.isNew && (
                                <Tag data-size="sm" data-color="warning">
                                  {t('hrRiskFactors.list.newQuestion')}
                                </Tag>
                              )}
                              {state.edited && (
                                <Tag data-size="sm" data-color="warning">
                                  {t('hrRiskFactors.list.editedQuestion')}
                                </Tag>
                              )}
                              {missing.length > 0 && (
                                <Tag data-size="sm" data-color="danger">
                                  {t('hrRiskFactors.list.missingTranslation', {
                                    languages: missing.map((language) => languageNameInText(language, t)).join(', '),
                                  })}
                                </Tag>
                              )}
                            </span>
                            {editing && (
                              <QuestionForm
                                id={`hr-rf-question-${index}-${questionIndex}`}
                                variant="edit"
                                initial={{ text: question.text, highIsGood: question.highIsGood }}
                                saving={saving}
                                onSubmit={saveQuestion}
                                onCancel={() => setTarget(null)}
                              />
                            )}
                          </span>
                          {!locked && !editing && (
                            <span className={classes.questionActions}>
                              <Button
                                variant="tertiary"
                                data-size="sm"
                                aria-label={t('hrRiskFactors.question.editLabel', { question: question.text })}
                                onClick={() => setTarget({ factorIndex: index, editIndex: questionIndex })}
                              >
                                {t('hrRiskFactors.question.edit')}
                              </Button>
                              <Button
                                variant="tertiary"
                                data-size="sm"
                                aria-label={t('hrRiskFactors.question.removeLabel', { question: question.text })}
                                onClick={() => onSave(withoutQuestion(factors, index, questionIndex))}
                              >
                                {t('hrRiskFactors.question.remove')}
                              </Button>
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                  {adding && (
                    <QuestionForm
                      id={`hr-rf-question-${index}`}
                      variant="add"
                      saving={saving}
                      onSubmit={saveQuestion}
                      onCancel={() => setTarget(null)}
                    />
                  )}
                  {!locked && (
                    <div className={classes.factorActions}>
                      {!adding && (
                        <Button variant="secondary" data-size="sm" onClick={() => setTarget({ factorIndex: index, editIndex: null })}>
                          {t('hrRiskFactors.question.add')}
                        </Button>
                      )}
                      <Button
                        variant="tertiary"
                        data-size="sm"
                        data-color="danger"
                        aria-label={t('hrRiskFactors.list.removeFactorLabel', { name: factor.name })}
                        onClick={() =>
                          onSave(withoutFactor(factors, index), () => {
                            setOpenIndex(null);
                            setTarget(null);
                          })
                        }
                      >
                        {t('hrRiskFactors.list.removeFactor')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};
